// src/services/labService.js
import apiClient from "./apiClient";
import axios from "axios";
import {
  formatPathItemFields,
  normalizePathBomItem,
  normalizePathBomList,
} from "../utils/nameFormatUtils";

const API_URL = `/lab`; // Use relative path since apiClient has baseURL

// Helper function to get environment variable
const getEnvVar = (key, defaultValue = null) => {
  if (typeof window !== 'undefined' && window.env && typeof window.env === 'object' && window.env[key]) {
    return window.env[key];
  }
  if (process.env && process.env[key]) {
    return process.env[key];
  }
  return defaultValue;
};

const BACKEND_URL = getEnvVar('REACT_APP_BACKEND_URL', 'http://localhost:8080/api');

export const getSampleCollections = async () => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_URL}/sample-collections`);
    return response.data;
  } catch (error) {
    console.error("Error fetching sample collections:", error);
    throw error;
  }
};

// Base table function for sample collections (not organization-specific)
export const getSampleCollectionsFromBaseTable = async () => {
  try {
    // Fetch from base sample collections table (not organization-specific)
    // Use direct axios call to bypass apiClient's organizationId interceptor
    console.log(`[getSampleCollectionsFromBaseTable] Fetching sample collections from base table...`);
    
    const token = localStorage.getItem("token");
    const apiUrl = `${BACKEND_URL}${API_URL}/sample-collections`;
    
    const response = await axios.get(apiUrl, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    
    const data = response.data;
    console.log(`[getSampleCollectionsFromBaseTable] ✅ Fetched ${Array.isArray(data) ? data.length : 0} sample collections from base table`);
    return data;
  } catch (error) {
    console.error("[getSampleCollectionsFromBaseTable] ❌ Error fetching sample collections:", error);
    throw error;
  }
};

export const getBOMs = async () => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_URL}/boms`);
    return normalizePathBomList(response.data);
  } catch (error) {
    console.error("Error fetching BOMs:", error);
    throw error;
  }
};

export const createBOM = async (payload) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.post(`${API_URL}/boms`, formatPathItemFields(payload));
    return normalizePathBomItem(response.data);
  } catch (error) {
    console.error("Error creating BOM:", error);
    throw error;
  }
};

export const updateBOM = async (id, payload) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.put(`${API_URL}/boms/${id}`, formatPathItemFields(payload));
    return normalizePathBomItem(response.data);
  } catch (error) {
    console.error("Error updating BOM:", error);
    throw error;
  }
};

// Base table functions (not organization-specific) for BOMs
export const getBOMsFromBaseTable = async () => {
  try {
    // Fetch from base BOM table (not organization-specific)
    // Use direct axios call to bypass apiClient's organizationId interceptor
    console.log(`[getBOMsFromBaseTable] Fetching BOMs from base BOM table...`);
    
    const token = localStorage.getItem("token");
    const apiUrl = `${BACKEND_URL}${API_URL}/boms`;
    
    const response = await axios.get(apiUrl, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    
    const data = response.data;
    console.log(`[getBOMsFromBaseTable] ✅ Fetched ${Array.isArray(data) ? data.length : 0} BOMs from base table`);
    return normalizePathBomList(data);
  } catch (error) {
    console.error("[getBOMsFromBaseTable] ❌ Error fetching BOMs:", error);
    throw error;
  }
};

export const createBOMInBaseTable = async (payload) => {
  try {
    // Create BOM in base BOM table (not organization-specific)
    // Use direct axios call to bypass apiClient's organizationId interceptor
    console.log(`[createBOMInBaseTable] Creating BOM in base BOM table...`);
    
    const token = localStorage.getItem("token");
    const apiUrl = `${BACKEND_URL}${API_URL}/boms`;
    
    const response = await axios.post(apiUrl, formatPathItemFields(payload), {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    
    console.log(`[createBOMInBaseTable] ✅ Successfully created BOM in base table`);
    return normalizePathBomItem(response.data);
  } catch (error) {
    console.error("[createBOMInBaseTable] ❌ Error creating BOM:", error);
    throw error;
  }
};

export const updateBOMInBaseTable = async (id, payload) => {
  try {
    // Update BOM in base BOM table (not organization-specific)
    // Use direct axios call to bypass apiClient's organizationId interceptor
    console.log(`[updateBOMInBaseTable] Updating BOM ${id} in base BOM table...`);
    
    const token = localStorage.getItem("token");
    const apiUrl = `${BACKEND_URL}${API_URL}/boms/${id}`;
    
    const response = await axios.put(apiUrl, formatPathItemFields(payload), {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    
    console.log(`[updateBOMInBaseTable] ✅ Successfully updated BOM in base table`);
    return normalizePathBomItem(response.data);
  } catch (error) {
    console.error("[updateBOMInBaseTable] ❌ Error updating BOM:", error);
    throw error;
  }
};